"use client"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Mail, User, Building } from "lucide-react"
import { useState } from "react"
import { submitDemoForm } from "@/app/actions/submit-form"
import { toast } from "sonner"

export function DemoForm({ className }: { className?: string }) {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [company, setCompany] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isValidEmail = (value: string) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(value)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!isValidEmail(email)) {
      setError("Please enter a valid work email")
      return
    }
    setError(null)
    setIsSubmitting(true)

    try {
      const formData = new FormData(e.currentTarget)
      const result = await submitDemoForm({ success: false, message: '' }, formData)

      if (result.success) {
        toast.success(result.message)
        setName("")
        setEmail("")
        setCompany("")
      } else {
        toast.error(result.message)
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${className}`}
    >
      <div className="relative">
        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
        <Input
          name="name"
          aria-label="Your name"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="rounded-full bg-white/50 backdrop-blur-md border border-gray/40 pl-10 focus:ring-2 focus:ring-[#1a85ca] focus:border-transparent"
        />
      </div>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
        <Input
          name="email"
          aria-label="Work email"
          type="email"
          placeholder="Work email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-full bg-white/50 backdrop-blur-md border border-gray/40 pl-10 focus:ring-2 focus:ring-[#1a85ca] focus:border-transparent"
        />
      </div>
      <div className="relative">
        <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
        <Input
          name="company"
          aria-label="Company"
          placeholder="Company"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className="rounded-full bg-white/50 backdrop-blur-md border border-gray/40 pl-10 focus:ring-2 focus:ring-[#1a85ca] focus:border-transparent"
        />
      </div>
      {error && (
        <p className="md:col-span-3 text-center text-[#EA3056] text-sm">{error}</p>
      )}
      <div className="md:col-span-3 flex justify-center">
        <Button 
          type="submit" 
          disabled={isSubmitting}
          className="group relative overflow-hidden rounded-full bg-[#1a85ca] hover:bg-[#1a85cc] px-6"
        >
          <span className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[linear-gradient(120deg,transparent,rgba(255,255,255,.25),transparent)] animate-pulse-slow" />
          {isSubmitting ? (
             <div className="flex items-center space-x-2">
               <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
               <span>Sending...</span>
             </div>
          ) : (
            <>
              <Mail className="mr-2 h-4 w-4" />
              Request a 30‑min demo
            </>
          )}
        </Button>
      </div>
      <p className="md:col-span-3 mt-4 text-sm text-gray-500 text-center">
        Prefer email? <a className="text-[#1a85ca] font-medium hover:underline" href="mailto:sales@octosoft.ai">sales@octosoft.ai</a>
      </p>
    </form>
  )
}
